import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api, ApiError } from '../lib/api';
import { useT, type TKey } from '../lib/i18n';
import { formatPhone } from './calendar/AppointmentPanel';
import { SidePanel } from './forms';
import { Button, ErrorNote } from './ui';

const FIELDS = ['firstName', 'lastName', 'fullName', 'phone', 'email', 'notes'] as const;
type Field = (typeof FIELDS)[number];
type Mapping = Partial<Record<Field, number | null>>;
type Row = { line: number; firstName: string; lastName: string | null; phone: string | null; email: string | null; action: 'create' | 'existing' | 'duplicate' };
type Preview = {
  headers: string[];
  mapping: Mapping;
  counts: { rows: number; create: number; existing: number; duplicate: number; invalid: number; warnings: number };
  sample: Row[];
  problems: Array<{ line: number; reason: string }>;
};
type Result = { batchId: string; created: number; filled: number; skipped: number };

/**
 * Excel often saves CSV in the old Windows character set; read as UTF-8 first and fall back when
 * that gives broken characters, so "Zoë" doesn't arrive as "ZoÃ«".
 */
async function readText(file: File) {
  const bytes = new Uint8Array(await file.arrayBuffer());
  const utf8 = new TextDecoder('utf-8').decode(bytes);
  return utf8.includes('�') ? new TextDecoder('windows-1252').decode(bytes) : utf8;
}

/** Klanten → Importeren: choose a file, check the preview and the columns, import, and undo if needed. */
export function ClientImport({ orgId, onClose }: { orgId: string; onClose: () => void }) {
  const t = useT();
  const qc = useQueryClient();
  const [csv, setCsv] = useState<string | null>(null);
  const [mapping, setMapping] = useState<Mapping | undefined>(undefined);
  const [existing, setExisting] = useState<'skip' | 'fill'>('skip');
  const [result, setResult] = useState<Result | null>(null);
  const [undone, setUndone] = useState<{ removed: number; kept: number } | null>(null);
  const err = (e: unknown) => (e instanceof ApiError ? e.message : e ? t('error.generic') : null);

  const preview = useMutation({ mutationFn: (body: { csv: string; mapping?: Mapping }) => api<Preview>(`/orgs/${orgId}/clients/import/preview`, { method: 'POST', body: JSON.stringify(body) }) });
  const run = useMutation({
    mutationFn: () => api<Result>(`/orgs/${orgId}/clients/import`, { method: 'POST', body: JSON.stringify({ csv, mapping: preview.data?.mapping, existing }) }),
    onSuccess: (r) => {
      setResult(r);
      void qc.invalidateQueries({ queryKey: ['clients', orgId] });
    },
  });
  const undo = useMutation({
    mutationFn: () => api<{ removed: number; kept: number }>(`/orgs/${orgId}/clients/import/${result!.batchId}/undo`, { method: 'POST' }),
    onSuccess: (r) => {
      setUndone(r);
      void qc.invalidateQueries({ queryKey: ['clients', orgId] });
    },
  });

  async function choose(file: File | undefined) {
    if (!file) return;
    const text = await readText(file);
    setCsv(text);
    setMapping(undefined);
    preview.mutate({ csv: text });
  }
  const remap = (field: Field, value: string) => {
    const next = { ...(preview.data?.mapping ?? mapping), [field]: value === '' ? null : Number(value) };
    setMapping(next);
    if (csv) preview.mutate({ csv, mapping: next });
  };
  const p = preview.data;
  const reason = (r: string) => (['no_name', 'phone', 'email', 'phone,email'].includes(r) ? t(`import.reason.${r}` as TKey) : r);

  return (
    <SidePanel open title={t('import.title')} onClose={onClose}>
      <div className="flex flex-col gap-5">
        {result ? (
          <section className="flex flex-col gap-3">
            <p className="display text-[30px] leading-tight">{t('import.done', { created: result.created })}</p>
            {result.filled > 0 && <p className="text-muted">{t('import.filled', { n: result.filled })}</p>}
            {undone ? (
              <p className="rounded-[var(--radius-control)] bg-subtle px-3 py-2 text-sm">
                {t('import.undone', { n: undone.removed })}
                {undone.kept > 0 && ` · ${t('import.kept', { n: undone.kept })}`}
              </p>
            ) : null}
            <ErrorNote message={err(undo.error)} />
            <div className="flex flex-wrap gap-2">
              <Button onClick={onClose}>{t('import.finish')}</Button>
              {!undone && result.created > 0 && (
                <Button variant="secondary" onClick={() => undo.mutate()} disabled={undo.isPending}>
                  {t('import.undo')}
                </Button>
              )}
            </div>
          </section>
        ) : (
          <>
            <p className="text-[15px] text-muted">{t('import.intro')}</p>
            <label className="flex cursor-pointer flex-col items-start gap-2 rounded-[var(--radius-card)] border border-dashed border-line bg-surface px-4 py-5 hover:bg-subtle">
              <span className="font-semibold">{csv ? t('import.another') : t('import.file')}</span>
              <input type="file" accept=".csv,text/csv,text/plain" onChange={(e) => void choose(e.target.files?.[0])} className="text-sm" />
            </label>
            {preview.isPending && <p className="text-muted">{t('import.reading')}</p>}
            <ErrorNote message={err(preview.error) ?? err(run.error)} />

            {p && (
              <>
                <section className="flex flex-col gap-2">
                  <h3 className="text-sm font-semibold">{t('import.columns')}</h3>
                  <p className="-mt-1 text-sm text-muted">{t('import.columnsHint')}</p>
                  <div className="grid grid-cols-[auto_minmax(0,1fr)] items-center gap-x-3 gap-y-2">
                    {FIELDS.map((f) => (
                      <label key={f} className="contents">
                        <span className="text-sm">{t(`import.field.${f}` as TKey)}</span>
                        <select value={p.mapping[f] ?? ''} onChange={(e) => remap(f, e.target.value)} className="min-h-10 rounded-[var(--radius-control)] border border-line bg-surface px-2 text-sm">
                          <option value="">{t('import.unused')}</option>
                          {p.headers.map((h, i) => (
                            <option key={i} value={i}>
                              {h || `#${i + 1}`}
                            </option>
                          ))}
                        </select>
                      </label>
                    ))}
                  </div>
                </section>

                <section className="flex flex-col gap-1 rounded-[var(--radius-card)] border border-line bg-surface p-4">
                  <p className="display text-[26px] leading-tight">{t('import.summary', { create: p.counts.create })}</p>
                  {p.counts.existing > 0 && <p className="text-sm text-muted">{t('import.existing', { n: p.counts.existing })}</p>}
                  {p.counts.duplicate > 0 && <p className="text-sm text-muted">{t('import.duplicate', { n: p.counts.duplicate })}</p>}
                  {p.counts.invalid > 0 && <p className="text-sm text-warning">{t('import.invalid', { n: p.counts.invalid })}</p>}
                </section>

                {p.counts.existing > 0 && (
                  <fieldset className="flex flex-col gap-2">
                    <legend className="mb-1 text-sm font-semibold">{t('import.existingChoice')}</legend>
                    {(['skip', 'fill'] as const).map((v) => (
                      <label key={v} className="flex items-start gap-2 text-[15px]">
                        <input type="radio" name="existing" checked={existing === v} onChange={() => setExisting(v)} className="mt-1 accent-[var(--color-ink)]" />
                        {t(v === 'skip' ? 'import.skip' : 'import.fill')}
                      </label>
                    ))}
                  </fieldset>
                )}

                <section className="flex flex-col gap-2">
                  <h3 className="text-sm font-semibold">{t('import.sample')}</h3>
                  <ul className="divide-y divide-line overflow-hidden rounded-[var(--radius-card)] border border-line bg-surface text-sm">
                    {p.sample.map((r) => (
                      <li key={r.line} className="flex items-center justify-between gap-3 px-3 py-2">
                        <span className="min-w-0">
                          <span className="block truncate font-medium">{[r.firstName, r.lastName].filter(Boolean).join(' ')}</span>
                          <span className="block truncate text-muted">{[r.phone && formatPhone(r.phone), r.email].filter(Boolean).join(' · ') || '—'}</span>
                        </span>
                        <span className={`tag shrink-0 ${r.action === 'create' ? 'bg-accent-soft text-accent-strong' : 'bg-subtle text-muted'}`}>{t(`import.status.${r.action}` as TKey)}</span>
                      </li>
                    ))}
                  </ul>
                </section>

                {p.problems.length > 0 && (
                  <section className="flex flex-col gap-1">
                    <h3 className="text-sm font-semibold">{t('import.problems')}</h3>
                    <ul className="flex flex-col gap-0.5 text-sm text-muted">
                      {p.problems.map((x, i) => (
                        <li key={i}>
                          {t('import.line', { n: x.line })}: {reason(x.reason)}
                        </li>
                      ))}
                    </ul>
                  </section>
                )}

                <Button onClick={() => run.mutate()} disabled={run.isPending || p.counts.create + (existing === 'fill' ? p.counts.existing : 0) === 0}>
                  {t('import.run', { n: p.counts.create })}
                </Button>
              </>
            )}
          </>
        )}
      </div>
    </SidePanel>
  );
}
